import type { ApiError } from "@handvero/shared";

/** Fehler der HandVero-API mit der Meldung, die direkt angezeigt werden kann. */
export class ApiFailure extends Error {
  constructor(
    readonly status: number,
    message: string,
    readonly code?: string,
  ) {
    super(message);
  }
}

async function request<T>(method: string, url: string, body?: unknown): Promise<T> {
  let res: Response;
  try {
    res = await fetch(url, {
      method,
      credentials: "same-origin",
      headers: body === undefined ? undefined : { "content-type": "application/json" },
      body: body === undefined ? undefined : JSON.stringify(body),
    });
  } catch {
    throw new ApiFailure(0, "Keine Verbindung zum Server. Bitte prüfen Sie Ihre Internetverbindung.");
  }
  const data = (await res.json().catch(() => ({}))) as T & Partial<ApiError>;
  if (!res.ok) throw new ApiFailure(res.status, data.error ?? "Da ist etwas schiefgelaufen. Bitte versuchen Sie es noch einmal.", data.code);
  return data;
}

export const api = {
  get: <T>(url: string) => request<T>("GET", url),
  post: <T = { ok: true }>(url: string, body: unknown = {}) => request<T>("POST", url, body),
  patch: <T>(url: string, body: unknown) => request<T>("PATCH", url, body),
  put: <T = { ok: true }>(url: string, body: unknown) => request<T>("PUT", url, body),
  del: <T = { ok: true }>(url: string, body?: unknown) => request<T>("DELETE", url, body),
};

/** Hochladen mit Fortschrittsanzeige (fetch kann den Upload-Fortschritt nicht melden). */
export function upload<T>(url: string, form: FormData, onProgress?: (share: number) => void): Promise<T> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open("POST", url);
    xhr.withCredentials = true;
    xhr.responseType = "json";
    xhr.upload.onprogress = (e) => e.lengthComputable && onProgress?.(e.loaded / e.total);
    xhr.onerror = () => reject(new ApiFailure(0, "Keine Verbindung zum Server. Bitte prüfen Sie Ihre Internetverbindung."));
    xhr.onload = () => {
      const data = (xhr.response ?? {}) as T & Partial<ApiError>;
      if (xhr.status >= 200 && xhr.status < 300) resolve(data);
      else reject(new ApiFailure(xhr.status, data.error ?? "Das Hochladen hat nicht geklappt. Bitte versuchen Sie es noch einmal.", data.code));
    };
    xhr.send(form);
  });
}

export function errorText(err: unknown): string {
  return err instanceof Error ? err.message : "Da ist etwas schiefgelaufen.";
}
